import React, { useState } from "react";
import styled from "styled-components";
import { P3Desk } from "../../styles/styles_text";
import { Card } from "../../components/ui/card";
import NewFilter from "../../assets/kinologyIcons/svg/new_filter.svg";
import WithFilter from "../../assets/kinologyIcons/svg/with_filter.svg";
import { FilterButton } from "../../styles/styles_input_pesquisas";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Pie,
	PieChart,
	XAxis,
	YAxis,
} from "recharts";
import {
	ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "../../components/ui/chart";
import SearchUsers from "./SearchUsers";
import { useDispatch, useSelector } from "react-redux";
import DatePickerWithRange from "./DataPicker";
import Filter from "./Filter";

type Orientation = "horizontal" | "vertical";

interface DataShema {
	name_risco: string;
	valor_risco: string;
	fill: string;
	usuarios: any[];
}

interface DonutChartProps {
	title: string;
	data: Array<DataShema> | [];
	onDonutChange: (selected: string[]) => void;
	defaultFilter: any;
}

const LegendaAlteracoes: React.FC = () => (
	<LegendaContainer>
		<LinhaLegenda>
			<Circulo cor="#7CFFA2" />
			<div>
				<TituloLegenda>Sem alterações</TituloLegenda>
				<TextoLegenda>
					Categoria na qual <b>NÃO houve alterações</b> nos relatórios de
					Assimetria e Relação IQ.
				</TextoLegenda>
			</div>
		</LinhaLegenda>
		<LinhaLegenda>
			<Circulo cor="#FFE066" />
			<div>
				<TituloLegenda>Alterações leves</TituloLegenda>
				<TextoLegenda>
					Alterações <b>consideráveis</b> com a Assimetria em 10-20% e/ou Relação
					IQ entre 40-50% ou 70-80%.
				</TextoLegenda>
			</div>
		</LinhaLegenda>
		<LinhaLegenda>
			<Circulo cor="#FFA800" />
			<div>
				<TituloLegenda>Alterações significativas</TituloLegenda>
				<TextoLegenda>
					Alterações <b>expressivas</b> com a Assimetria entre 20-35% e/ou Relação
					IQ entre 30-40% ou 80-90%.
				</TextoLegenda>
			</div>
		</LinhaLegenda>
		<LinhaLegenda>
			<Circulo cor="#FF3B1D" />
			<div>
				<TituloLegenda>Alterações graves</TituloLegenda>
				<TextoLegenda>
					Alterações <b>altamente expressivas</b>, com a Assimetria acima de 35%
					e/ou Relação IQ abaixo de 30% ou acima de 90%.
				</TextoLegenda>
			</div>
		</LinhaLegenda>
	</LegendaContainer>
);

const AlteracoesEncontradas: React.FC<DonutChartProps> = ({
	title,
	data,
	onDonutChange,
	defaultFilter,
}) => {
	const [isTooltipVisible, setIsTooltipVisible] = useState(false);

	const [with_filter, set_with_filter] = useState(false);
	const onFilterChange = (selected: any) => {
		const aux_filter = Object.entries(selected);
		set_with_filter(aux_filter.length > 0 ? true : false);
		onDonutChange(selected);
	};

	const chartConfig = {
		valor_risco: {
			label: "Visitors",
		},

		"Fora do padrão": {
			label: "Padrão esperado",
			color: "hsl(var(--chart-5))",
		},
	} satisfies ChartConfig;

	function _renderBar() {
		return (

			<ChartContainer
				config={chartConfig}
				style={{ height: "250px", width: "70%" }}
			>
				<BarChart accessibilityLayer data={data}>
					<CartesianGrid vertical={false} strokeDasharray="5 5" />
					<XAxis
						dataKey="name_risco"
						tickLine={false}
						tickMargin={10}
						axisLine={false}
						textAnchor="end"
						interval={0}
						tick={({ x, y, payload }) => {
							const words = payload.value.split(' ');
							return (
								<g transform={`translate(${x},${y})`}>
									<text
										x={0}
										y={0}
										style={{ fontSize: 11, fill: "#63677A", textAlign: 'center', justifyContent: 'center' }}
									>
										{words.map((word: any, index: any) => (
											<tspan
												key={index}
												x={-25}
												dy={index === 0 ? 0 : 15}
												dx={0}
											>
												{word}
											</tspan>
										))}
									</text>
								</g>
							);
						}}
					/>
					<YAxis
						tickLine={false}
						tickMargin={10}
						axisLine={false}
						tick={({ x, y, payload }) => {
							return (
								<g transform={`translate(${x},${y})`}>
									<text
										x={0}
										y={0}
										dy={3} // deslocamento vertical
										dx={10}
										textAnchor="end"
										style={{
											fontSize: 12,
											fill: "rgba(128, 128, 128, 0.69)",
										}} // Altere o tamanho da fonte aqui
									>
										{payload.value}
									</text>
								</g>
							);
						}}
					/>
					<ChartTooltip
						cursor={{ fill: 'rgba(0, 0, 0, 0)' }}
						active={isTooltipVisible}
						content={<SearchUsers />}
					/>
					<Bar dataKey="valor_risco" fill="fill" radius={8} barSize={40} onMouseEnter={() => setIsTooltipVisible(true)} />
				</BarChart>
			</ChartContainer>

		);
	}

	const assimetrias_dynalabs = useSelector(
		(state: any) => state.dashboard.assimetrias_dynalabs
	);

	return (
		<Card
			style={{
				display: "flex",
				flexDirection: "row",
				backgroundColor: "#FFFFFF",
				width: "100%",
				marginBottom: "0.75rem",
				marginTop: "0.75rem",
			}}
			onMouseEnter={() => setIsTooltipVisible(false)}
		>
			<div
				style={{
					width: "60%",
					position: "relative",
					display: "flex",
					flexDirection: "column",
					padding: "1rem",
				}}
			>
				<P3Desk
					onMouseEnter={() => setIsTooltipVisible(false)}
					style={{
						textAlign: "center",
						paddingLeft: "2rem",
						paddingRight: "2rem",
						fontSize: "1rem",
						color: "#63677A",
					}}
				>
					{title}
				</P3Desk>
				<P3Desk
					onMouseEnter={() => setIsTooltipVisible(false)}
					style={{
						textAlign: "center",
						margin: '0.5rem'
					}}
				>
					{assimetrias_dynalabs?.assimetrias?.length} avaliados
				</P3Desk>

				<div style={{ position: "absolute", top: "1rem", right: "1rem" }}>
					<Filter
						icon={with_filter ? WithFilter : NewFilter}
						onFilterChange={onFilterChange}
						defaultFilter={defaultFilter}
						GraphType={title}
					/>
				</div>

				<div
					style={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					{_renderBar()}
				</div>
			</div>
			<div
				style={{
					width: "40%",
					backgroundColor: "#F8F8F8",
					borderRadius: "10px",
					borderTopLeftRadius: "28px",
					borderBottomLeftRadius: "28px",
				}}
			>
				<LegendaAlteracoes />
			</div>
		</Card>
	);
};

const Color = styled.div``;

const LegendaContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 2rem 1.5rem;
`;

const LinhaLegenda = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 0.8rem;
`;

const Circulo = styled.div<{ cor: string }>`
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background: ${({ cor }) => cor};
	margin-top: 4px;
	flex-shrink: 0;
`;

const TituloLegenda = styled.span`
	font-weight: 600;
	font-size: 0.8rem;
	color: #5a5e6a;
`;

const TextoLegenda = styled.span`
	font-size: 0.7rem;
	color: #5a5e6a;
	display: block;
`;

export default AlteracoesEncontradas;
